import { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { get } from 'lodash';

import ApiErrorsFormatter from '../Utils/ApiErrorsFormatter';
import { QueryConfig } from '../Services/queriesConfig';

interface useApiQueryProps<T> {
  query: QueryConfig<T>;
  options?: any;
  onSuccess?: (response: any) => void;
  onError?: (errors: any) => void;
  onSettled?: (response: any) => void;
}

function useApiQuery<T>({
  query,
  options,
  onSuccess = () => {},
  onError = () => {},
  onSettled = () => {},
}: useApiQueryProps<T>) {
  const [errors, setErrors] = useState();

  const { data, ...rest } = useQuery({
    queryKey: query.queryKey,
    queryFn: query.queryFn,
    throwOnError: true,
    enabled: true,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    ...options,
  });

  // Handle success/error callbacks via useEffect (callbacks removed in v5)
  useEffect(() => {
    if (rest.isSuccess && data) {
      if ((data as any).ok) {
        onSuccess(get(data, 'data.data'));
      } else {
        const apiErrors = ApiErrorsFormatter(get((data as any)?.data, 'errors'));
        setErrors(apiErrors);
        onError(apiErrors);
      }
      onSettled(data);
    }
  }, [data, rest.isSuccess]);

  return {
    data: get(data, 'data.data'),
    paging: get(data, 'data.paging') || { total: 0, total_pages: 0 },
    count: get(data, 'data.paging.total') || 0,
    errors,
    ...rest,
  };
}

export default useApiQuery;
